import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { MapPin, Square, BedDouble, Euro, Search, Home, Building, Layers, TreePine, ArrowRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  apartment: Home, house: Home, villa: Home,
  land: TreePine, office: Building, commercial: Building,
  garage: Building, studio: Home, project: Layers
};

export default function ListingsPage() {
  const { t } = useTranslation();
  const { market, marketLink } = useMarket();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: 'all', category: 'all', city: 'all', neighborhood: 'all', search: '' });

  useEffect(() => {
    axios.get(`${API}/listings${market !== 'en' ? `?market=${market || 'bg'}` : ''}`).then(r => {
      setListings((r.data || []).filter(l => l.status !== 'draft' && l.status !== 'sold' && l.status !== 'rented'));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Dynamically extract unique cities from listings
  const cities = [...new Set(
    listings
      .map(l => l.location?.trim())
      .filter(Boolean)
  )].sort();

  // Dynamically extract unique neighborhoods from listings
  const neighborhoods = [...new Set(
    listings
      .map(l => l.neighborhood?.trim())
      .filter(Boolean)
  )].sort();

  const filtered = (listings || []).filter(l => {
    if (filters.type !== 'all' && l.listing_type !== filters.type) return false;
    if (filters.category !== 'all' && l.category !== filters.category) return false;
    if (filters.city !== 'all' && l.location !== filters.city) return false;
    if (filters.neighborhood !== 'all' && l.neighborhood !== filters.neighborhood) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      return (
        l.title?.toLowerCase().includes(q) ||
        l.location?.toLowerCase().includes(q) ||
        l.neighborhood?.toLowerCase().includes(q) ||
        l.description?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const formatPrice = (price, type) => {
    const formatted = new Intl.NumberFormat('bg-BG').format(price);
    return type === 'rent' ? `${formatted} EUR${t('listings.per_month')}` : `${formatted} EUR`;
  };

  const hasActiveFilters = filters.type !== 'all' || filters.category !== 'all' || filters.city !== 'all' || filters.neighborhood !== 'all' || filters.search !== '';

  const clearFilters = () => setFilters({ type: 'all', category: 'all', city: 'all', neighborhood: 'all', search: '' });

  const LISTING_TYPES_MAP = { sale: t('listings.sale'), rent: t('listings.rent') };
  const CATEGORIES_MAP = {
    apartment: t('cat.apartment'), house: t('cat.house'), villa: t('cat.villa'),
    land: t('cat.land'), office: t('cat.office'), commercial: t('cat.commercial'),
    garage: t('cat.garage'), studio: t('cat.studio'), project: t('cat.project')
  };

  return (
    <div className="min-h-screen bg-background" data-testid="listings-page">
      <SEO title={t('nav.listings')} description={t('seo.listings_desc')} url="/listings" />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">{t('listings.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">{t('listings.subtitle')}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filter bar */}
          <div className="flex flex-wrap gap-3 mb-6 p-4 bg-white rounded-xl border border-border shadow-sm">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('listings.search')}
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-10"
                data-testid="listing-search-input"
              />
            </div>

            {/* Type filter */}
            <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
              <SelectTrigger className="w-40" data-testid="listing-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listings.all')}</SelectItem>
                <SelectItem value="sale">{t('listings.sale')}</SelectItem>
                <SelectItem value="rent">{t('listings.rent')}</SelectItem>
              </SelectContent>
            </Select>

            {/* Category filter */}
            <Select value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))}>
              <SelectTrigger className="w-44" data-testid="listing-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listings.all_categories')}</SelectItem>
                {Object.entries(CATEGORIES_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* City / Location filter - only show if there are multiple cities */}
            {cities.length > 1 && (
              <Select value={filters.city} onValueChange={(v) => setFilters(f => ({ ...f, city: v }))}>
                <SelectTrigger className="w-44" data-testid="listing-city-filter">
                  <SelectValue placeholder={t('listings.all_cities')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('listings.all_cities')}</SelectItem>
                  {cities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Neighborhood filter - only show if there are neighborhoods */}
            {neighborhoods.length > 0 && (
              <Select value={filters.neighborhood} onValueChange={(v) => setFilters(f => ({ ...f, neighborhood: v }))}>
                <SelectTrigger className="w-48" data-testid="listing-neighborhood-filter">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder={t('listings.all_neighborhoods')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('listings.all_neighborhoods')}</SelectItem>
                  {neighborhoods.map(n => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Clear filters button */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground gap-1.5">
                <X className="w-3.5 h-3.5" />
                {t('listings.clear_filters')}
              </Button>
            )}
          </div>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {filters.type !== 'all' && (
                <Badge variant="secondary" className="gap-1 pr-1 cursor-pointer" onClick={() => setFilters(f => ({ ...f, type: 'all' }))}>
                  {LISTING_TYPES_MAP[filters.type]} <X className="w-3 h-3" />
                </Badge>
              )}
              {filters.category !== 'all' && (
                <Badge variant="secondary" className="gap-1 pr-1 cursor-pointer" onClick={() => setFilters(f => ({ ...f, category: 'all' }))}>
                  {CATEGORIES_MAP[filters.category]} <X className="w-3 h-3" />
                </Badge>
              )}
              {filters.city !== 'all' && (
                <Badge variant="secondary" className="gap-1 pr-1 cursor-pointer" onClick={() => setFilters(f => ({ ...f, city: 'all' }))}>
                  <MapPin className="w-3 h-3" /> {filters.city} <X className="w-3 h-3" />
                </Badge>
              )}
              {filters.neighborhood !== 'all' && (
                <Badge variant="secondary" className="gap-1 pr-1 cursor-pointer bg-[#1B5E37]/10 text-[#1B5E37]" onClick={() => setFilters(f => ({ ...f, neighborhood: 'all' }))}>
                  <MapPin className="w-3 h-3" /> {filters.neighborhood} <X className="w-3 h-3" />
                </Badge>
              )}
              {filters.search && (
                <Badge variant="secondary" className="gap-1 pr-1 cursor-pointer" onClick={() => setFilters(f => ({ ...f, search: '' }))}>
                  "{filters.search}" <X className="w-3 h-3" />
                </Badge>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground mb-6">
            {t('listings.found')} <strong>{filtered.length}</strong> {t('listings.results')}
            {filters.neighborhood !== 'all' && (
              <span className="ml-1 text-[#1B5E37] font-medium">— {t('listings.in_neighborhood')} {filters.neighborhood}</span>
            )}
          </p>

          {/* Listings grid */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-lg text-muted-foreground">{t('listings.no_results')}</p>
              <p className="text-sm text-muted-foreground mt-1">{t('listings.try_filters')}</p>
              {hasActiveFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />{t('listings.clear_filters')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(listing => {
                const CategoryIcon = CATEGORY_ICONS[listing.category] || Home;
                return (
                  <Link key={listing.id} to={marketLink(`/listings/${listing.id}`)} className="group" data-testid={`listing-card-${listing.id}`}>
                    <div className="bg-white rounded-xl overflow-hidden border border-border hover:border-[#1B5E37] hover:shadow-lg transition-all">
                      <div className="relative h-52 overflow-hidden">
                        <img src={listing.images?.[0] || '/og-image.jpg'} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3 flex gap-2">
                          <Badge className={listing.listing_type === 'sale' ? 'bg-[#1B5E37] text-white' : 'bg-blue-600 text-white'}>
                            {LISTING_TYPES_MAP[listing.listing_type] || listing.listing_type}
                          </Badge>
                          <Badge variant="secondary" className="bg-white/90 text-foreground">
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {CATEGORIES_MAP[listing.category] || listing.category}
                          </Badge>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-[#1B5E37] transition-colors line-clamp-1">{listing.title}</h3>
                        <div className="mb-3">
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            {listing.location}
                            {listing.neighborhood && (
                              <span className="ml-1 text-xs font-medium text-[#1B5E37] bg-[#1B5E37]/8 px-1.5 py-0.5 rounded-full">
                                кв. {listing.neighborhood}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                          {listing.area && (<span className="flex items-center gap-1"><Square className="w-3.5 h-3.5" />{listing.area} {t('property.area')}</span>)}
                          {listing.rooms && (<span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{listing.rooms} {t('property.rooms')}</span>)}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-[#1B5E37] flex items-center gap-1">
                            <Euro className="w-4 h-4" />{formatPrice(listing.price, listing.listing_type)}
                          </span>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-[#1B5E37] transition-colors" />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
